import React, { useEffect, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useMsal } from '@azure/msal-react'
import LogoutIcon from '@mui/icons-material/Logout'
import Tooltip from '@mui/material/Tooltip'

import signInIcon from '../../../../assets/person_edit.png'
import { IRootState } from '../../../../redux/reducers'
import { IdentityState } from '../../../../redux/reducers/initialState'
import { useIdentity } from '../../identity/useIdentity'
import { usePermissions } from '../../permission/usePermissions'
import { isScreenSizeSmall } from '../../utils'

import UserAvatar from './UserAvatar'

import '@microsoft/mgt'

/**
 * Props for the SignInButton component.
 */
type SignInButtonProps = {
  signInButtonColor?: string
  userAvatarBackGroundColor?: string
  userAvatarFontColor?: string
  userAvatarClickable?: boolean
}

/**
 * SignInButton component displays a sign-in button or user avatar with a dropdown menu.
 *
 * @param {SignInButtonProps} props - The props for the SignInButton component.
 * @returns {JSX.Element} The SignInButton component.
 */
export const SignInButton: React.FC<SignInButtonProps> = ({
  signInButtonColor = 'white',
  userAvatarBackGroundColor = 'white',
  userAvatarFontColor = '#0096d6',
  userAvatarClickable = true,
}) => {
  const { accounts } = useMsal()
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const identity: IdentityState | null = useSelector(
    (state: IRootState) => state.identity,
  )

  const { getUserPermissions } = usePermissions()
  const { signIn, signOut, signInSilent } = useIdentity()

  useEffect(() => {
    if (accounts.length > 0) {
      signInSilent()
    }
  }, [accounts])

  useEffect(() => {
    if (identity !== null && identity !== undefined) {
      getUserPermissions()
    } else {
      signInSilent()
    }
  }, [identity])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  return (
    <div>
      <header>
        {!identity ? (
          <button
            className="responsive-font"
            onClick={signIn}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: signInButtonColor,
              border: 'none',
              background: 'none',
              fontWeight: isScreenSizeSmall() ? 400 : 500,
              flexDirection: isScreenSizeSmall() ? 'column' : 'row',
            }}
            data-testid="sign-in-button"
          >
            <img
              alt="Sign In Icon"
              src={signInIcon}
              className="d-inline-block align-top"
              style={{
                marginRight: '4px',
                height: isScreenSizeSmall() ? '5vh' : 35,
              }}
            />
            {t('sign_in')}
          </button>
        ) : (
          <Dropdown
            className="responsive-font"
            align="end"
            style={{
              marginRight: isScreenSizeSmall() ? '10px' : '1px',
              marginBottom: isScreenSizeSmall() ? '4px' : '0',
            }}
            show={isOpen}
            ref={dropdownRef}
          >
            {userAvatarClickable ? (
              <Dropdown.Toggle
                as={({ children, onClick }: any) => (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      onClick(e)
                    }}
                    style={{
                      ...customDropdownToggle,
                      background: 'none',
                      border: 'none',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        'rgba(0, 0, 0, 0.05)')
                    }
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = ''
                      e.currentTarget.style.background = 'none'
                    }}
                    onMouseUp={() => setIsOpen(!isOpen)}
                  >
                    {children}
                  </button>
                )}
                id="dropdown-custom-components"
                data-testid="user-avatar-toggle"
              >
                <UserAvatar
                  name={identity.name}
                  color={userAvatarFontColor}
                  bgcolor={userAvatarBackGroundColor}
                />
              </Dropdown.Toggle>
            ) : (
              <UserAvatar
                name={identity.name}
                color={userAvatarFontColor}
                bgcolor={userAvatarBackGroundColor}
                data-testid="user-avatar-toggle"
              />
            )}
            {userAvatarClickable && (
              <Dropdown.Menu
                className="responsive-font"
                flip={false}
                style={dropdownMenuStyle}
              >
                <Dropdown.Item
                  as="div"
                  style={{
                    ...dropdownMenuItemStyle,
                    ...dropdownMenuItemNoHoverStyle,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal',
                      width: isScreenSizeSmall() ? '80vw' : '231px',
                      padding: '0px 4px 0px 4px',
                    }}
                  >
                    <Tooltip
                      title={
                        <span style={{ fontSize: '16px' }}>
                          {identity?.name}
                        </span>
                      }
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'black',
                          },
                        },
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          width: '100%',
                        }}
                      >
                        {identity?.name}
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        <span style={{ fontSize: '16px' }}>
                          {identity?.emailAddress}
                        </span>
                      }
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'black',
                          },
                        },
                      }}
                    >
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'inline-block',
                          color: '#A3A3A3',
                          width: '100%',
                        }}
                      >
                        {identity?.emailAddress}
                      </span>
                    </Tooltip>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider style={dropdownDividerStyle} />
                <Dropdown.Item
                  as="div"
                  style={{
                    ...dropdownMenuItemStyle,
                    ...dropdownMenuItemNoHoverStyle,
                  }}
                >
                  <button
                    onClick={signOut}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <LogoutIcon
                      style={{ marginRight: '8px', marginLeft: '-5px' }}
                      sx={
                        isScreenSizeSmall()
                          ? { height: '5vh', width: '5vw' }
                          : {}
                      }
                    />
                    {t('sign_out')}
                  </button>
                </Dropdown.Item>
              </Dropdown.Menu>
            )}
          </Dropdown>
        )}
      </header>
    </div>
  )
}

const customDropdownToggle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  transition: 'background-color 0.3s',
}

const dropdownMenuStyle: React.CSSProperties = {
  marginTop: '10px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  borderRadius: '15px',
  border: 'none',
}

const dropdownMenuItemStyle: React.CSSProperties = {
  fontSize: 'inherit',
  width: 'calc(100% - 20px)',
  height: '100%',
  padding: '8px 12px',
  margin: '2px 10px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderRadius: '10px',
}

const dropdownMenuItemNoHoverStyle: React.CSSProperties = {
  backgroundColor: 'inherit !important',
  color: 'inherit !important',
}

const dropdownDividerStyle: React.CSSProperties = {
  marginInline: '10px',
}
